import { useState, useEffect, ChangeEvent } from 'react';
import { Link } from 'react-router';
import { Card, CollasableCard } from './Cards';
import InputField from './InputField';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import Converters from './Converters';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';

interface BeerStyleSummary {
  id: number;
  styleId: string;
  name: string;
  translatedName: string | null;
  type: string;
  categoryName: string;
  translatedCategoryName: string | null;
  ogMin: number | null;
  ogMax: number | null;
  fgMin: number | null;
  fgMax: number | null;
  ibuMin: number | null;
  ibuMax: number | null;
  abvMin: number | null;
  abvMax: number | null;
  colorMin: number | null;
  colorMax: number | null;
  tags: string | null;
}

interface Filters {
  name: string;
  abvMin: string;
  abvMax: string;
  ibuMin: string;
  ibuMax: string;
  blgMin: string;
  blgMax: string;
  ebcMin: string;
  ebcMax: string;
}

function formatRange(min: number | null, max: number | null, decimals = 1): string {
  if (min == null && max == null) return '—';
  if (min != null && max != null) return `${min.toFixed(decimals)}–${max.toFixed(decimals)}`;
  if (min != null) return `${min.toFixed(decimals)}+`;
  return `≤${max!.toFixed(decimals)}`;
}

function sgToBlg(sg: number | null): number | null {
  if (sg == null) return null;
  return Converters.gravityToPlato(sg);
}

function srmToEbc(srm: number | null): number | null {
  if (srm == null) return null;
  return Converters.srmToEbc(srm);
}

export default function BeerStyles() {
  const { t } = useTranslation();
  const [data, setData] = useState<BeerStyleSummary[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<Filters>({
    name: '', abvMin: '', abvMax: '',
    ibuMin: '', ibuMax: '', blgMin: '', blgMax: '',
    ebcMin: '', ebcMax: '',
  });

  useEffect(() => {
    document.title = t('styles.pageTitle');
  }, [t]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('lang', i18n.resolvedLanguage ?? i18n.language);
    if (filters.name) params.set('name', filters.name);
    if (filters.abvMin) params.set('abvMin', filters.abvMin);
    if (filters.abvMax) params.set('abvMax', filters.abvMax);
    if (filters.ibuMin) params.set('ibuMin', filters.ibuMin);
    if (filters.ibuMax) params.set('ibuMax', filters.ibuMax);
    if (filters.blgMin) params.set('blgMin', filters.blgMin);
    if (filters.blgMax) params.set('blgMax', filters.blgMax);
    if (filters.ebcMin) params.set('ebcMin', filters.ebcMin);
    if (filters.ebcMax) params.set('ebcMax', filters.ebcMax);

    setIsLoaded(false);
    fetch(`/api/styles?${params.toString()}`)
      .then(res => res.json())
      .then((result: BeerStyleSummary[]) => {
        setData(result);
        setIsLoaded(true);
      })
      .catch((err: Error) => {
        setError(err);
        setIsLoaded(true);
      });
  }, [filters, i18n.language]);

  function handleFilterChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  }

  if (error) return <p className="text-destructive text-sm">{t('common.error', { message: error.message })}</p>;

  return (
    <div>
      <CollasableCard title={t('styles.infoTitle')}>
        <p className="text-muted-foreground text-sm">
          {t('styles.infoText')}
        </p>
      </CollasableCard>

      <Card title={t('styles.filtersTitle')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          <div className="sm:col-span-2 lg:col-span-3 xl:col-span-5">
            <label className="font-medium text-foreground text-xs">{t('styles.filterName')}</label>
            <Input
              type="text"
              name="name"
              value={filters.name}
              onChange={handleFilterChange}
              placeholder={t('styles.filterNamePlaceholder')}
            />
          </div>
          <InputField name="abvMin" label={t('styles.filterAbvMin')} unit="%" step="0.1" value={filters.abvMin} onChange={handleFilterChange} />
          <InputField name="abvMax" label={t('styles.filterAbvMax')} unit="%" step="0.1" value={filters.abvMax} onChange={handleFilterChange} />
          <InputField name="ibuMin" label={t('styles.filterIbuMin')} unit="IBU" step="1" value={filters.ibuMin} onChange={handleFilterChange} />
          <InputField name="ibuMax" label={t('styles.filterIbuMax')} unit="IBU" step="1" value={filters.ibuMax} onChange={handleFilterChange} />
          <InputField name="blgMin" label={t('styles.filterBlgMin')} unit="°BLG" step="0.1" value={filters.blgMin} onChange={handleFilterChange} />
          <InputField name="blgMax" label={t('styles.filterBlgMax')} unit="°BLG" step="0.1" value={filters.blgMax} onChange={handleFilterChange} />
          <InputField name="ebcMin" label={t('styles.filterEbcMin')} unit="EBC" step="1" value={filters.ebcMin} onChange={handleFilterChange} />
          <InputField name="ebcMax" label={t('styles.filterEbcMax')} unit="EBC" step="1" value={filters.ebcMax} onChange={handleFilterChange} />
        </div>
      </Card>

      <Card title={t('styles.resultsTitle', { count: data.length })}>
        {!isLoaded ? (
          <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
        ) : data.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('styles.noResults')}</p>
        ) : (
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-3 font-medium">{t('styles.colId')}</th>
                  <th className="pb-2 pr-3 font-medium">{t('styles.colName')}</th>
                  <th className="pb-2 pr-3 font-medium">{t('styles.colCategory')}</th>
                  <th className="pb-2 pr-3 font-medium text-right">{t('styles.colBlgOg')}</th>
                  <th className="pb-2 pr-3 font-medium text-right">{t('styles.colAbv')}</th>
                  <th className="pb-2 pr-3 font-medium text-right">{t('styles.colIbu')}</th>
                  <th className="pb-2 font-medium text-right">{t('styles.colEbc')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map(style => (
                  <tr key={style.id} className="border-b last:border-0 hover:bg-accent/50 transition-colors">
                    <td className="py-2 pr-3">
                      <Badge variant="secondary" className="font-mono text-xs">{style.styleId}</Badge>
                    </td>
                    <td className="py-2 pr-3">
                      <Link
                        to={`/styles/${style.id}`}
                        className="text-primary hover:underline font-medium"
                      >
                        {style.translatedName || style.name}
                      </Link>
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{style.translatedCategoryName || style.categoryName}</td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {formatRange(sgToBlg(style.ogMin), sgToBlg(style.ogMax))}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {formatRange(style.abvMin, style.abvMax)}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {formatRange(style.ibuMin, style.ibuMax, 0)}
                    </td>
                    <td className="py-2 text-right font-mono">
                      {formatRange(srmToEbc(style.colorMin), srmToEbc(style.colorMax), 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
